/** @format */
import { TRoom } from '@/@ducks/room/declarations';
import { selectorRoomIndex, selectorRoomListType } from '@/ducks/general/selectors';
import { GTMAddToCart, GTMAskInfoS1, GTMBookARoom } from '@/ducks/googleTagManager/helpers/google-tag-manager';
import { selectorFilters } from '@/ducks/listing/selectors';
import { useAppTranslation } from '@/i18n/declarations';
import { findLocalizedData } from '@/i18n/utils/find-localized-data';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { TLocale, TLocalesObject } from '@/utils/gatsby/types';
import { getGtmRoomType } from '@/utils/helpers/getGtmRoomType';
import { utilityIsRoomAvailableFrom } from '@/utils/helpers/isRoomAvailableFrom';
import { utilityGetPrice } from '@/utils/helpers/utilityGetPrice';
import { utilityGTMBuildStringByListingActiveFilters } from '@/utils/helpers/utilityGTMBuildStringByListingActiveFilters';
import { zendeskRequestInfoUrlGenerator } from '@/utils/helpers/zendeskLinksGenerator';
import { FormattedMessage, Link } from 'gatsby-plugin-intl';
import snakeCase from 'lodash/snakeCase';
import { nanoid } from 'nanoid';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  selectorBookingAvailability,
  selectorIsNotSalableOnline,
  selectorIsRoomNotAvailable,
} from '../../../../../@ducks/bookingNew/selectors';

interface BookButtonProps {
  locale: TLocale;
  room: TRoom;
}

const CAMPUS_ROOM_CODES = ['mi-1352-01-a', 'mi-1353-01-a'];
const CAMPUS_LINKS: TLocalesObject<string> = {
  it: 'https://studenthousing.joivy.com/reservations.php?residence=Milano%20Lepontina',
  en: 'https://studenthousing.joivy.com/en/reservations.php?residence=Milano%20Lepontina',
  fr: 'https://studenthousing.joivy.com/en/reservations.php?residence=Milano%20Lepontina',
  es: 'https://studenthousing.joivy.com/en/reservations.php?residence=Milano%20Lepontina',
};

const BookButton: React.FC<BookButtonProps> = ({ locale, room }) => {
  const { t, intl } = useAppTranslation();
  const dispatch = useAppDispatch();
  const isRoomNotAvailable = useAppSelector(selectorIsRoomNotAvailable);
  const isRoomNotSalableOnline = useAppSelector(selectorIsNotSalableOnline);
  const availability = useAppSelector(selectorBookingAvailability);
  const { getFormattedPrice, getDimension } = utilityGetPrice({ availability, room: room as any });
  const roomIndex = useAppSelector(selectorRoomIndex);
  const filters = useAppSelector(selectorFilters);
  const roomListType = useAppSelector(selectorRoomListType);

  const isCampusRoom = CAMPUS_ROOM_CODES.includes(room.code.toLowerCase());
  const bookingUrl = `/${t('url_booking')}/?roomCode=${encodeURIComponent(room.code.toUpperCase())}`;
  const isFrenchCountryCode = room.countryCode === 'FRA' || room.countryCode === 'FR';
  const requestInfoHref = useMemo(() => {
    if (isCampusRoom) return CAMPUS_LINKS[locale];
    if (isFrenchCountryCode) return 'mailto:coliving.france@joivy.com';
    return zendeskRequestInfoUrlGenerator({
      city: room.city.name,
      roomCode: room.code,
      price: getFormattedPrice(),
      countryCode: room.countryCode,
    });
  }, [isFrenchCountryCode, isCampusRoom, room, locale, getFormattedPrice]);

  const handleBook = () => {
    const price = getFormattedPrice();
    const orderBy = filters.orderBy === 'null' || !filters.orderBy ? 'availability_asc' : filters.orderBy;
    const activeCategoryFilter = utilityGTMBuildStringByListingActiveFilters(filters);
    const localizedEntry = findLocalizedData(intl.locale as TLocale, room as any as TRoom);
    const isRoomAvailableFrom = utilityIsRoomAvailableFrom(availability);
    const isRoomNotAvailable = availability ? !availability?.availableFrom || availability?.negotiating : true;
    const roomType = getGtmRoomType(room.flat.type);

    const currentBookingId = nanoid();

    GTMAddToCart(
      room.code,
      price,
      snakeCase(localizedEntry?.title),
      `${snakeCase(room.city.nameEn.toLowerCase())}_${snakeCase(room.district.name.toLowerCase())}`,
      roomType,
      getDimension,
      isRoomNotAvailable ? 'not_available' : isRoomAvailableFrom ? 'available_on_next_weeks' : 'available',
      roomIndex ?? '',
      orderBy,
      activeCategoryFilter,
      roomListType ?? 'search_result',
    );

    GTMBookARoom(room.code, price, currentBookingId);

    setTimeout(() => {
      dispatch({
        type: 'BOOKING_SET_ROOM',
        payload: undefined,
      });
      dispatch({
        type: 'BOOKING_SET_FORM_ID',
        payload: currentBookingId,
      });
    }, 1);
  };

  if (isRoomNotAvailable) {
    return (
      <StyledBookButton as="button" disabled>
        {t('unavailable')}
      </StyledBookButton>
    );
  }

  if (isRoomNotSalableOnline) {
    return (
      <StyledBookButton
        href={requestInfoHref}
        target="_blank"
        external
        onClick={() => GTMAskInfoS1({ monthlyRate: getFormattedPrice(), roomCode: room.code })}
      >
        <FormattedMessage id="request_more_info" />
      </StyledBookButton>
    );
  }

  return (
    <StyledBookButton to={bookingUrl} onClick={handleBook}>
      <FormattedMessage id="pdp.book" />
    </StyledBookButton>
  );
};

export default BookButton;

const StyledBookButton = styled(({ external, ...props }) => (external ? <a {...props} /> : <Link {...props} />))`
  --bg-color: ${({ theme }) => theme.colors.joivy['teal-light']};
  --color: ${({ theme }) => theme.colors.joivy.white};
  width: 100%;
  display: block;
  text-align: center;
  color: var(--color);
  text-decoration: none;
  cursor: pointer;
  padding: 0.75rem 0.9375rem;
  background-color: var(--bg-color);
  border: none;
  transition: background-color 0.3s ease, border 0.3s ease;
  border-radius: 0.625rem;
  font-size: clamp(1.125rem, 2.2917rem + 2.3148vw, 1.375rem);
  letter-spacing: clamp(0.03375rem, 0.0313rem + 0.0111vw, 0.04125rem);
  font-weight: 700;
  padding-block: 1.85rem;

  &:hover,
  &:active,
  &:focus {
    --bg-color: ${({ theme }) => theme.colors.joivy['teal-dark']};
  }

  &:disabled {
    --bg-color: ${({ theme }) => theme.colors.alternativeGrey};
    cursor: default;
  }
`;
